import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'
import { RequireAuth } from '@dss/auth'
import { AdminShell } from '../components/AdminShell'
import { DistributorTabBar } from '../features/distributors/DistributorTabBar'
import { useDistributorDetail } from '../features/distributors/usePlatformDistributors'
import { redirectIfNotPlatformAdmin } from '../lib/platformAdminGuard'
import '../features/distributors/platform-workspace.css'

/** `/distributors/$id` — layout-route карточки тенанта (PA-12, D17 §2.3).
 *
 *  Раньше этот route рендерил `DistributorDetailScreen` напрямую вместе с
 *  AdminShell. PA-12 разбил карточку на 6 табов (overview / orders /
 *  sync-events / users / settings / audit-log) — общий shell, breadcrumbs и
 *  tab-bar теперь живут здесь, а каждый child-таб рендерит только свой
 *  content внутри `<Outlet/>`.
 *
 *  Permission-gating: `redirectIfNotPlatformAdmin` на уровне layout-route
 *  наследуется всеми child-маршрутами (TanStack Router запускает `beforeLoad`
 *  родителя перед child). Permission `platform.distributors.view` /
 *  `platform.audit.view` проверяется уже внутри конкретных tab-экранов
 *  (разные табы — разные permissions).
 *
 *  Breadcrumbs: вытаскиваем displayName через `useDistributorDetail(id)` на
 *  уровне layout. Child-таб может вызвать тот же query — `@tanstack/react-query`
 *  дедуплицирует. Невалидный id (NaN) — inline error message, как было до PA-12. */
export const Route = createFileRoute('/distributors/$id')({
  beforeLoad: () => {
    redirectIfNotPlatformAdmin(redirect)
  },
  component: DistributorDetailLayoutRoute,
})

function DistributorDetailLayoutRoute() {
  const { id } = Route.useParams()
  const numericId = Number(id)
  if (!Number.isFinite(numericId)) {
    // Невалидный id в URL — отображаем встроенное сообщение об ошибке.
    return (
      <RequireAuth>
        <div style={{ padding: 24, color: 'var(--color-text-secondary)' }}>
          Некорректный идентификатор дистрибьютора в URL.
        </div>
      </RequireAuth>
    )
  }
  return (
    <RequireAuth>
      <DistributorDetailLayout distributorId={numericId} />
    </RequireAuth>
  )
}

export function DistributorDetailLayout({ distributorId }: { distributorId: number }) {
  // Тот же query, что использует Overview-таб — react-query дедуплицирует
  // по queryKey, так что fetch один.
  const detailQuery = useDistributorDetail(distributorId)
  const displayName = (!detailQuery.isError && detailQuery.data?.displayName) || `#${distributorId}`
  return (
    <AdminShell
      active="distributors"
      crumbs={
        <>
          <a
            href="/admin/distributors"
            style={{ color: 'var(--color-text-secondary)', textDecoration: 'none' }}
          >
            Дистрибьюторы
          </a>
          <span aria-hidden="true" style={{ margin: '0 6px', color: 'var(--color-text-tertiary)' }}>
            ›
          </span>
          <strong>{displayName}</strong>
        </>
      }
    >
      <div className="pa-distributor-detail">
        <DistributorTabBar distributorId={distributorId} />
        <Outlet />
      </div>
    </AdminShell>
  )
}
